// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Shared dataset-payload query.
 *
 * Fetching a payload is also what *resolves which run* is on screen: several runs
 * can sit at one document version, and the server picks one. Everything that then
 * addresses that run's artifacts — annotated PDF, annotated DOCX — must use the
 * run this query resolved, not re-derive it, or the two can land on different runs.
 *
 * So this is a hook rather than a bare fetch: the component showing the payload
 * and the toolbar downloading its artifacts call it with the same arguments, get
 * the same cache entry (one request, React Query dedupes), and therefore the same
 * `run`.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import type { NodeApiClient } from '@/app-shell/services'
import { fetchDatasetByKey, type DatasetResponse } from '../services/dataset'

export interface UseDatasetPayloadParams {
  apiClient: NodeApiClient
  orgId: string
  wsId: string
  documentId: string
  agentId: string
  /** Unbound dataset slots have no key; the query stays idle. */
  datasetKey?: string
  version: number
  /**
   * Diff-mode entry point: which comparison to open. A diff result is stored
   * against the newer version of its pair, so v1→v3 and v2→v3 both live at v3
   * and the version alone doesn't say which one is wanted. Only the initial
   * resolution needs this — downstream artifact requests carry the resolved run.
   */
  comparedTo?: number
  groupId?: string
  enabled?: boolean
}

/** Fetch one agent's dataset payload, keyed so every caller shares one request. */
export function useDatasetPayload<T = Record<string, unknown>>({
  apiClient,
  orgId,
  wsId,
  documentId,
  agentId,
  datasetKey,
  version,
  comparedTo,
  groupId,
  enabled = true,
}: UseDatasetPayloadParams): UseQueryResult<DatasetResponse<T>> {
  return useQuery({
    queryKey: [
      'dataset-payload',
      orgId,
      wsId,
      documentId,
      agentId,
      datasetKey,
      version,
      comparedTo ?? null,
      groupId ?? null,
    ],
    queryFn: () => fetchDatasetByKey<T>(
      apiClient, orgId, wsId, documentId, agentId, datasetKey!, version, groupId,
      { comparedTo },
    ),
    enabled: enabled && Boolean(datasetKey),
  })
}
